import { Info } from "lucide-react";
import {
  ErrorCodeToCategory,
  ErrorCategory,
  PublishErrorCodeType,
} from "../constants/errorCodes";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface StatusBadgeProps {
  status:
    | "generating"
    | "published"
    | "publishing"
    | "pre-processing"
    | "scheduled"
    | "draft"
    | "failed"
    | "pending"
    | "mixed";
  mixedStatusCounts?: Record<string, number>;
  errorInfo?: {
    code?: string;
    message?: string;
    timestamp: number;
    retryCount: number;
    maxAttempts: number;
    retryable?: boolean;
    cancelled?: boolean;
  };
}

// Retry configuration from server/src/index.ts
const RETRY_CONFIG = {
  maxAttempts: 5,
  minBackoff: 300, // 5 minutes in seconds
  maxBackoff: 3600, // 1 hour in seconds
  maxDoublings: 5,
};

const calculateTimeUntilRetry = (error?: {
  timestamp: number;
  retryCount: number;
  retryable?: boolean;
}) => {
  if (
    !error ||
    error.retryable === false ||
    !error.timestamp ||
    error.retryCount >= RETRY_CONFIG.maxAttempts
  ) {
    return null;
  }

  // Calculate backoff time based on retry count
  const backoffSeconds = Math.min(
    RETRY_CONFIG.minBackoff *
      Math.pow(2, Math.min(error.retryCount - 1, RETRY_CONFIG.maxDoublings)),
    RETRY_CONFIG.maxBackoff
  );
  const nextRetryTime = error.timestamp + backoffSeconds * 1000;
  const now = Date.now();

  if (nextRetryTime <= now) {
    return null;
  }

  const timeUntilRetry = Math.floor((nextRetryTime - now) / 1000);
  const minutes = Math.floor(timeUntilRetry / 60);
  const seconds = timeUntilRetry % 60;

  return minutes > 0 ? `${minutes}m` : `${seconds}s`;
};

export function StatusBadge({
  status,
  mixedStatusCounts,
  errorInfo,
}: StatusBadgeProps) {
  const variants = {
    generating: "bg-yellow-100 text-yellow-800",
    published: "bg-green-100 text-green-800",
    scheduled: "bg-blue-100 text-blue-800",
    publishing: "bg-blue-50 text-blue-600",
    "pre-processing": "bg-indigo-50 text-indigo-600",
    draft: "bg-gray-100 text-gray-800",
    failed: "bg-red-100 text-red-800",
    pending: "bg-yellow-100 text-yellow-800",
    mixed: "bg-purple-100 text-purple-800",
  };

  // Inline breakdown for mixed status, e.g. "1 published · 1 failed"
  const mixedLabel =
    status === "mixed" && mixedStatusCounts
      ? Object.entries(mixedStatusCounts)
          .filter(([_, count]) => count > 0)
          .map(([s, count]) => `${count} ${s}`)
          .join(" · ")
      : null;

  // Generate tooltip content based on status and error category
  const getTooltipContent = () => {
    if (status === "pre-processing") {
      return "Processing media files according to platform requirements";
    }

    if (status === "failed" && errorInfo) {
      if (errorInfo.cancelled) {
        return "Retry cancelled";
      }

      if (errorInfo.retryable === false) {
        return errorInfo.message || "Publishing failed";
      }

      const errorCode = errorInfo.code as PublishErrorCodeType;
      const category = errorCode ? ErrorCodeToCategory[errorCode] : ErrorCategory.SYSTEM;

      // Show category-based message
      switch (category) {
        case ErrorCategory.AUTH:
          return "Account reconnection required";
        case ErrorCategory.RATE_LIMIT:
          const retryTime = calculateTimeUntilRetry(errorInfo);
          return retryTime ? `Rate limited - retry in ${retryTime}` : "Rate limited";
        case ErrorCategory.VALIDATION:
          return "Post requires editing";
        case ErrorCategory.SYSTEM:
        default:
          if (errorInfo.retryCount >= RETRY_CONFIG.maxAttempts) {
            return `Failed after ${errorInfo.retryCount} retries`;
          }
          const sysRetryTime = calculateTimeUntilRetry(errorInfo);
          if (sysRetryTime) {
            return `System error - retry in ${sysRetryTime}`;
          }
          return `Failed after ${errorInfo.retryCount} retries`;
      }
    }

    return null;
  };

  const tooltipContent = getTooltipContent();

  // Only show info icon if we have tooltip content
  const showInfoIcon = !!tooltipContent;

  const label = mixedLabel || status.charAt(0).toUpperCase() + status.slice(1);

  // If we have tooltip content, wrap the badge in a tooltip
  if (showInfoIcon) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="cursor-help">
            <Badge
              variant="outline"
              className={cn(variants[status], "flex items-center gap-1")}
            >
              {label}
              <Info className="h-4 w-4" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" align="center" className="z-50 max-w-xs">
            <p>{tooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Otherwise, just return the badge without the info icon
  return (
    <Badge variant="outline" className={variants[status]}>
      {label}
    </Badge>
  );
}
